import { z } from "zod";

/**
 * Admin-driven pozvánka uživatele (V069) — formulářové schéma.
 *
 * Admin zadá Jméno, Příjmení, e-mail a roli; po uložení se odešle onboarding
 * e-mail s linkem. Mikroskupinová pozvánka (sub-admin) je jen speciální případ.
 *
 * JSON payload backendu je snake_case (REQ-API-012).
 */

export const INVITABLE_ROLES = ["endUser", "subAdmin", "tenantAdmin"] as const;
export type InvitableRole = (typeof INVITABLE_ROLES)[number];

export const INVITABLE_ROLE_LABELS: Record<InvitableRole, string> = {
  endUser: "Uživatel",
  subAdmin: "Sub-admin (mikroskupina)",
  tenantAdmin: "Admin tenantu",
};

export const userInviteSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(2, "Jméno musí mít alespoň 2 znaky.")
    .max(120, "Jméno je příliš dlouhé."),
  lastName: z
    .string()
    .trim()
    .min(2, "Příjmení musí mít alespoň 2 znaky.")
    .max(120, "Příjmení je příliš dlouhé."),
  email: z
    .string()
    .trim()
    .min(1, "E-mail je povinný.")
    .email("Neplatný formát e-mailu."),
  role: z.enum(INVITABLE_ROLES, { message: "Role je povinná." }),
  // Cílový tenant pozvánky (UC-005) — prázdné = tenant volajícího (globalAdmin).
  tenantId: z.string().optional(),
});

export type UserInviteInput = z.infer<typeof userInviteSchema>;

export interface UserInviteApiPayload {
  first_name: string;
  last_name: string;
  email: string;
  role: InvitableRole;
  tenant_id?: string;
}

export function toUserInviteApiPayload(input: UserInviteInput): UserInviteApiPayload {
  return {
    first_name: input.firstName,
    last_name: input.lastName,
    email: input.email,
    role: input.role,
    ...(input.tenantId ? { tenant_id: input.tenantId } : {}),
  };
}

export type UserInvitationStatus =
  | "pending"
  | "onboarded"
  | "revoked"
  | "expired";

export const USER_INVITATION_STATUS_LABELS: Record<UserInvitationStatus, string> = {
  pending: "Čeká na aktivaci",
  onboarded: "Aktivováno",
  revoked: "Zrušeno",
  expired: "Vypršelo",
};

export interface UserInvitationRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: InvitableRole;
  status: UserInvitationStatus;
  expires_at: string;
  created_at: string;
  onboarded_at: string | null;
}
